<template>
  <div id="taxonomy-app" class="product-page">
    <!-- Add a transient alert banner for "No more unreviewed" -->
    <transition name="fade">
      <div v-if="showNoMoreUnreviewed" class="alert-banner">
        No more unreviewed data left!
      </div>
    </transition>

    <!-- Main Content -->
    <main>
      <div class="main-content">
        <!-- Catalog View -->
        <div v-if="currentView === 'catalog'">
          <Catalog
            :products="products"
            :brands="brands"
            :dressCategories="attributeFilters.dressCategory.values['Dress Category'] || []"
            :manorTaxonomyAttributes="attributeFilters.manorTaxonomy"
            :alliumTaxonomyAttributes="attributeFilters.alliumTaxonomy"
            :flaggedAttributesList="flaggedAttributesList"
            :selectedFlaggingStrategy="selectedFlaggingStrategy"
            :selectedLanguage="selectedLanguage"
            @open-product="openProductPage"
            @navigate="navigateTo"
          />
        </div>

        <!-- Taxonomy Editor View -->
        <div v-else-if="currentView === 'taxonomyEditor'">
          <TaxonomyEditor
            :attributesData="attributeFilters"
            @navigate="navigateTo"
            @updateAttributes="updateAttributes"
          />
        </div>

        <!-- Product View -->
        <div v-else-if="currentView === 'product' && currentProductIndex !== null">
          <button class="back-button" @click="goToCatalog">← Back to Catalog</button>

          <!-- Right-side panel for toggles -->
          <div class="right-panel">
            <!-- Review Mode Toggle -->
            <button
              class="toggle-button"
              :class="{ active: reviewMode }"
              @click="toggleReviewMode"
            >
              <span v-if="reviewMode">Review Mode ON</span>
              <span v-else>Enter Review Mode</span>
            </button>

            <!-- Additional toggles only in review mode -->
            <div v-if="reviewMode" class="toggles-container">
              <!-- Skip Reviewed -> fetch next unreviewed item -->
              <button
                class="toggle-button"
                :class="{ active: skipReviewed }"
                @click="toggleSkipReviewed"
              >
                Skip Reviewed
              </button>

              <!-- Show Reviewed Data toggle, only if currently reviewed -->
              <button
                v-if="isCurrentlyReviewed"
                class="toggle-button"
                :class="{ active: showReviewedData }"
                @click="showReviewedData = !showReviewedData; applyReviewFilters();"
              >
                Show Reviewed Data
              </button>

              <!-- Only Show Edited Data -> fetch next unreviewed or something similar -->
              <button
                class="toggle-button"
                :class="{ active: onlyShowEditedData }"
                @click="toggleOnlyShowEditedData"
              >
                Only Show Edited Data
              </button>
              <button class="toggle-button" @click="openAddNotePopup">Add Note</button>
            </div>
          </div>

          <!-- The popup -->
          <div v-if="showAddNotePopup" class="modal-overlay">
            <div class="modal-content">
              <h3>Add Note</h3>
              <textarea 
                v-model="reviewNote"
                rows="5"
                cols="50"
                placeholder="Type your notes here..."
              ></textarea>
              <div class="modal-buttons">
                <button @click="saveNote">Save Note</button>
                <button @click="closeAddNotePopup">Cancel</button>
              </div>
            </div>
          </div>

          <div class="images-and-controls">
            <div class="images-container">
              <div class="panel">
                <product-images
                  :images="currentBeforeData.images"
                  :thumbnails="currentBeforeData.thumbnails"
                ></product-images>
              </div>

              <div class="page-navigation">
                <button
                  @click="previousProduct"
                  :disabled="isPreviousDisabled"
                  class="nav-button"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="icon icon-tabler icon-tabler-chevron-left"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path stroke="none" d="M0 0h24V0h24z" fill="none" />
                    <polyline points="15 6 9 12 15 18" />
                  </svg>
                </button>

                <div class="navigation-text">
                  <span class="article-number-text">Article No: {{ currentProductId }}</span>
                  <div class="variant-number-text">Variant No: {{ currentVariantId }}</div>
                  <div class="web-link">
                    <a :href="currentVariantData['Manor URL Link']" target="_blank">Web Link</a>
                  </div>
                </div>

                <button
                  @click="nextProduct"
                  :disabled="isNextDisabled"
                  class="nav-button"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    class="icon icon-tabler icon-tabler-chevron-right"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    stroke-width="2"
                    stroke="currentColor"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  >
                    <path stroke="none" d="M0 0h24V0h24z" fill="none" />
                    <polyline points="9 6 15 12 9 18" />
                  </svg>
                </button>
              </div>

              <!-- Brand Filter Drop-down -->
              <div class="brand-filter">
                <label for="brand">Brand:</label>
                <select v-model="selectedBrand" @change="onBrandChange" id="brand">
                  <option value="">All Brands</option>
                  <option v-for="brand in brands" :key="brand" :value="brand">
                    {{ brand }}
                  </option>
                </select>
              </div>

              <!-- Attribute Filters -->
              <div class="attribute-filter">
                <label for="attribute-filter-type">Attribute Filter Type:</label>
                <select
                  v-model="selectedAttributeFilter.type"
                  @change="onAttributeFilterTypeChange"
                  id="attribute-filter-type"
                >
                  <option value="">Select Filter Type</option>
                  <option value="dressCategory">Dress Category</option>
                  <option value="manorTaxonomy">Manor Taxonomy Attributes</option>
                  <option value="alliumTaxonomy">AlliumAI Taxonomy Attributes</option>
                </select>
              </div>

              <div v-if="selectedAttributeFilter.type" class="attribute-filter">
                <label for="attribute-name">Attribute Name:</label>
                <select
                  v-model="selectedAttributeFilter.attribute"
                  @change="onAttributeNameChange"
                  id="attribute-name"
                >
                  <option value="">Select Attribute</option>
                  <option
                    v-for="attrName in attributeFilters[selectedAttributeFilter.type].attributes"
                    :key="attrName"
                    :value="attrName"
                  >
                    {{ attrName }}
                  </option>
                </select>
              </div>

              <div v-if="selectedAttributeFilter.attribute" class="attribute-filter">
                <label for="attribute-value">Attribute Value:</label>
                <select
                  v-model="selectedAttributeFilter.value"
                  @change="applyAttributeFilter"
                  id="attribute-value"
                >
                  <option value="">Select Value</option>
                  <option
                    v-for="attrValue in attributeFilters[selectedAttributeFilter.type].values[selectedAttributeFilter.attribute]"
                    :key="attrValue"
                    :value="attrValue"
                  >
                    {{ attrValue }}
                  </option>
                </select>
              </div>

              <!-- Variant Search -->
              <div class="variant-search">
                <label for="variant-search-input">Search Variant No:</label>
                <input
                  id="variant-search-input"
                  v-model="variantSearchInput"
                  @keyup.enter="onVariantSearch"
                  placeholder="Enter Variant Number"
                />
                <button @click="onVariantSearch">Go</button>
              </div>

              <!-- Language Selector -->
              <div class="language-selector">
                <label for="language">Language:</label>
                <select v-model="selectedLanguage" @change="changeLanguage" id="language">
                  <option v-for="lang in languages" :key="lang" :value="lang">{{ lang }}</option>
                </select>
              </div>

              <!-- Data Toggle -->
              <div class="data-toggle">
                <button class="toggle-button" @click="toggleEditedData">
                  {{ isEditedData ? 'Show Original Data' : 'Show Edited Data' }}
                </button>
              </div>

              <!-- Flagging Strategy Selector -->
              <div class="flagging-strategy-selector">
                <label for="flagging-strategy">Flagging Strategy:</label>
                <select
                  v-model="selectedFlaggingStrategy"
                  @change="changeFlaggingStrategy"
                  id="flagging-strategy"
                >
                  <option value="None">None</option>
                  <option
                    v-for="strategy in flaggingStrategies"
                    :key="strategy"
                    :value="strategy"
                  >
                    {{ formatOptionLabel(strategy) }}
                  </option>
                </select>
              </div>

              <!-- Flagged Attribute Filter -->
              <div class="flagged-attribute-filter">
                <label for="flagged-attribute">Filter by Flagged Attribute:</label>
                <select
                  v-model="selectedFlaggedAttribute"
                  @change="applyBrandAndAttributeFilters"
                  id="flagged-attribute"
                >
                  <option value="">Select Attribute</option>
                  <option
                    v-for="attr in flaggedAttributesList"
                    :key="attr"
                    :value="attr"
                  >
                    {{ formatOptionLabel(attr) }}
                  </option>
                </select>
              </div>
            </div> <!-- Closing images-and-controls -->

            <!-- Details and Attributes -->
            <div class="content-container">
              <div class="comparison-container">
                <div class="comparison-section">
                  <h2 class="section-title">
                    Before
                    <label class="toggle-switch">
                      <input type="checkbox" v-model="beforeUpdatedSelected">
                      <span class="slider"></span>
                    </label>
                    <span class="toggle-label">Show Updated Data</span>
                  </h2>
                  <!-- Before Product Details Panel -->
                  <div class="panel">
                    <product-details
                      :data="currentBeforeData"
                      :availableColors="availableColors"
                      :availableSizes="availableSizes"
                      :selectedColor="selectedColor"
                      :selectedSize="selectedSize"
                      @colorSelected="selectColor"
                      @sizeSelected="selectSize"
                    ></product-details>
                  </div>
                  <div class="panel">
                    <product-attributes
                      :originalAttributes="currentBeforeData.attributes"
                      :attributesSources="currentBeforeData.attributesSources"
                      header="Attributes"
                    ></product-attributes>
                  </div>
                </div>

                <!-- After Section -->
                <div class="comparison-section">
                  <h2 class="section-title">After</h2>
                  <!-- After Product Details Panel -->
                  <div class="panel">
                    <product-details
                      :data="currentAfterData"
                      :availableColors="afterAvailableColors"
                      :availableSizes="availableSizes"
                      :selectedColor="selectedColor"
                      :selectedSize="selectedSize"
                      is-after
                      :edited-details="editedDetails"
                      :is-edited-data="isEditedData"
                      @detailEdited="handleDetailEdited"
                      @colorSelected="selectColor"
                      @sizeSelected="selectSize"
                    ></product-details>
                  </div>

                  <!-- Manor Taxonomy Attributes Panel -->
                  <div class="panel">
                    <div class="source-filter">
                      <label for="manor-attribute-source-filter">Manor Attribute Source Selector:</label>
                      <select
                        v-model="selectedManorAttributeSource"
                        id="manor-attribute-source-filter"
                      >
                        <option value="">No Filter</option>
                        <option value="Existing">Existing</option>
                        <option value="Image">Image</option>
                        <option value="Vendor">Vendor</option>
                        <option value="External">External</option>
                      </select>
                    </div>

                    <div class="reweighted-selector">
                      <label for="manor-reweighted">Select Prediction Method:</label>
                      <select
                        v-model="selectedManorReweightedMethod"
                        @change="changeManorReweightedMethod"
                        id="manor-reweighted"
                      >
                        <option value="None">None</option>
                        <option
                          v-for="method in reweightedMethods"
                          :key="method"
                          :value="method"
                        >
                          {{ formatOptionLabel(method) }}
                        </option>
                      </select>
                    </div>

                    <h3 class="manor-title">Manor Taxonomy Attributes</h3>
                    <!-- Two-column layout with confidence indicators in review mode -->
                    <div v-if="reviewMode" class="manor-attributes-grid-improved">
                      <div
                        v-for="(obj, key) in editedManorAttributes"
                        :key="key"
                        class="manor-attribute-row-improved"
                      >
                        <label :for="key" class="manor-attribute-label-improved">{{ key }}</label>
                        <div class="attribute-row-content-improved">
                          <select :id="key" v-model="obj.value" class="manor-attribute-select-improved">
                            <option v-for="option in getManorOptions(key)" :key="option" :value="option">
                              {{ option }}
                            </option>
                            <option value=""></option>
                          </select>
                          <span
                            class="confidence-indicator"
                            :class="confidenceClass(obj.confidence)"
                            @click="cycleConfidence(key)"
                            title="Click to change confidence"
                          ></span>
                        </div>
                      </div>
                    </div>
                    <div v-else>
                      <product-attributes
                        :originalAttributes="currentAfterData.manorTaxonomyAttributes"
                        :attributesSources="currentAfterData.manorTaxonomyAttributesSources"
                        :flaggedAttributes="manorFlaggedAttributes"
                        header="Manor Taxonomy Attributes"
                        :attributesMatchingDefault="currentAfterData.manorAttributesMatchingDefault"
                        :selectedSource="selectedManorAttributeSource"
                      ></product-attributes>
                    </div>
                  </div>

                  <!-- AlliumAI Taxonomy Attributes Panel -->
                  <div class="panel">
                    <div class="source-filter">
                      <label for="allium-attribute-source-filter">AlliumAI Attribute Source Selector:</label>
                      <select
                        v-model="selectedAlliumAttributeSource"
                        id="allium-attribute-source-filter"
                      >
                        <option value="">No Filter</option>
                        <option value="Existing">Existing</option>
                        <option value="Image">Image</option>
                        <option value="Vendor">Vendor</option>
                        <option value="External">External</option>
                      </select>
                    </div>

                    <div class="reweighted-selector">
                      <label for="allium-reweighted">Select Prediction Method:</label>
                      <select
                        v-model="selectedAlliumReweightedMethod"
                        @change="changeAlliumReweightedMethod"
                        id="allium-reweighted"
                      >
                        <option value="None">None</option>
                        <option
                          v-for="method in reweightedMethods"
                          :key="method"
                          :value="method"
                        >
                          {{ formatOptionLabel(method) }}
                        </option>
                      </select>
                    </div>

                    <product-attributes
                      :originalAttributes="currentAfterData.attributes"
                      :editedAttributes="editedAttributes"
                      :attributesSources="currentAfterData.attributesSources"
                      :flaggedAttributes="alliumFlaggedAttributes"
                      header="AlliumAI Taxonomy Attributes"
                      :is-edited-data="isEditedData"
                      @attributeEdited="handleAttributeEdited"
                      :attributesMatchingDefault="currentAfterData.attributesMatchingDefault"
                      :selectedSource="selectedAlliumAttributeSource"
                    ></product-attributes>
                  </div>

                  <!-- External Data Panel -->
                  <div class="panel">
                    <external-data 
                      :external-matches="currentAfterData.externalMatches"
                    ></external-data>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-else class="loading-state">
          <p>Loading products...</p>
        </div>
      </div>
    </main>

    <footer class="footer"></footer>

    <!-- Snackbar for save feedback -->
    <div v-if="showSnackbar" :class="['snackbar', snackbarColor]">
      {{ snackbarMessage }}
    </div>
  </div>
</template>

<script>
import Catalog from '@/components/taxonomy-editor/Catalog.vue';
import TaxonomyEditor from '@/components/taxonomy-editor/TaxonomyEditor.vue';
import ProductImages from '@/components/taxonomy-editor/ProductImages.vue';
import ProductDetails from '@/components/taxonomy-editor/ProductDetails.vue';
import ProductAttributes from '@/components/taxonomy-editor/ProductAttributes.vue';
import ExternalData from '@/components/taxonomy-editor/ExternalData.vue';
import Login from '@/components/taxonomy-editor/Login.vue';
import '@mdi/font/css/materialdesignicons.css'

const FUNCTION_URL = "https://data-reviewer-844472556697.us-central1.run.app"; // replace with your actual function URL

export default {
  name: 'TaxonomyEditorApp',
  components: {
    Catalog,
    TaxonomyEditor,
    ProductImages,
    ProductDetails,
    ProductAttributes,
    ExternalData,
    Login,
  },
  data() {
    return {
      showNoMoreUnreviewed: false, // triggers the banner
      showAddNotePopup: false,
      reviewNote: '',
      snackbarMessage: '',
      showSnackbar: false,
      snackbarColor: 'success', // could be 'success' or 'error'
      confidenceOrder: ['certain', 'pretty_sure', 'not_sure'],
      manor_taxonomy_en: {
        ClothingType: ["Evening dresses", "Cocktail dresses", "Knit dresses", "Denim dresses", "Summer dresses", "Casual dresses", "Lace dresses", "Dirndl"],
        Material: ["Cotton", "Linen", "Wool", "Viscose", "Silk", "Leather", "Polyester", "Lyocell", "Cashmere"],
        Length: ["Knee-length", "Maxi", "Midi", "Mini", "Calf-length", "3/4-length"],
        Neckline: ["Round neck", "V-neck", "Boat neckline", "Halter neck", "Sweetheart", "Back neckline", "Cache-coeur", "Square neckline", "Button up", "Zip up"],
        CollarType: ["Turtleneck", "Stand-up collar", "Henley", "Shawl collar", "Waterfall", "Hood", "Revere collar", "Bow collar", "Turn-down collar", "Troyer", "Hood", "Peter Pan collar"],
        SleeveLength: ["Short sleeve", "Long sleeve", "3/4 sleeve", "Sleeveless", "Half sleeve"],
        Pattern: ["Solid", "Checkered", "Polka dot", "Animal", "Camouflage", "Paisley", "Ethnic", "Striped", "Houndstooth", "Pinstripe", "Color-block", "Gradient", "Herringbone", "Floral", "Ornaments", "All-over print"],
        Fit: ["Body fit", "Classic fit", "Comfort fit", "Loose fit", "Regular fit", "Relaxed fit", "Skinny fit", "Slim fit", "Straight fit", "Tailored fit"],
        Occasion: ["Prom", "Casual", "Wedding", "Business", "Festive", "Party", "Beach", "Christmas"],
        SpecialSizes: ["Plus size", "Petite", "Tall", "Maternity"]
      },
      reviewMode: false,
      skipReviewed: true,
      onlyShowEditedData: false,
      showReviewedData: true,
      reviewedMap: {}, 
      editedManorAttributes: {},
      selectedFlaggedAttribute: '', 
      flaggedAttributesList: [],
      selectedAttributeSource: '',
      selectedManorAttributeSource: '',
      selectedAlliumAttributeSource: '',
      currentView: 'taxonomyEditor',
      isAuthenticated: true,
      products: {},
      allProductIds: [],
      filteredProductIds: [],
      currentProductIndex: null,
      selectedLanguage: 'english',
      selectedVariantId: '',
      selectedColor: '',
      selectedSize: '',
      brands: [],
      selectedBrand: '',
      variantSearchInput: '',
      beforeUpdatedSelected: false,
      selectedManorReweightedMethod: 'priority_sorting',
      selectedAlliumReweightedMethod: 'priority_sorting',
      selectedFlaggingStrategy: 'disagreement_existing_image',
      isEditedData: false,
      hasEdits: false,
      editedAttributes: {},
      editedDetails: {},
      attributeFilters: {
        dressCategory: {
          attributes: [],
          values: {},
        },
        manorTaxonomy: {
          attributes: [],
          values: {},
        },
        alliumTaxonomy: {
          attributes: [],
          values: {},
        },
      },
      selectedAttributeFilter: {
        type: '',
        attribute: '',
        value: '',
      },
    };
  },
  computed: {
    isCurrentlyReviewed() {
      // A product is “reviewed” if reviewedMap[productId]?.reviewed === true
      return this.currentProductId && this.reviewedMap[this.currentProductId]?.reviewed;
    },
    reweightedMethods() {
      return ['priority_sorting', 'voting_mode', 'voting_weighted', 'priority_image'];
    },
    flaggingStrategies() {
      return ['no_multi_source', 'no_structured_source', 'no_source', 'disagreement', 'disagreement_existing_image'];
    },
    currentAfterData() {
      const variantData = this.currentVariantData;
      if (!variantData) {
        return this.emptyProductData();
      }

      let selectedAfterData;
      if (this.beforeUpdatedSelected && variantData.after_updated) {
        selectedAfterData = variantData.after_updated;
      } else if (variantData.after) {
        selectedAfterData = variantData.after;
      } else {
        return this.emptyProductData();
      }

      const afterData = selectedAfterData[this.selectedLanguage];
      if (!afterData) {
        return this.emptyProductData();
      }

      const clonedAfterData = JSON.parse(JSON.stringify(afterData));

      if (this.selectedAlliumReweightedMethod !== 'None') {
        const reweightedAttributes =
          afterData.predictions_reweighted?.attributes?.[this.selectedAlliumReweightedMethod];
        if (reweightedAttributes) {
          clonedAfterData.attributes = reweightedAttributes;
        }
      }

      if (this.selectedManorReweightedMethod !== 'None') {
        const reweightedManorAttributes =
          afterData.predictions_reweighted?.manor_taxonomy_attributes?.[this.selectedManorReweightedMethod];
        if (reweightedManorAttributes) {
          clonedAfterData.manor_taxonomy_attributes = reweightedManorAttributes;
        }
      }

      const fieldsToExclude = [
        'productTitle',
        'productDescription',
        'bulletPoints',
        'primaryColorHexCode',
        'primaryColor',
        'allColors',
      ];

      const defaultAttributes = afterData.attributes || {};
      const defaultManorAttributes = afterData.manor_taxonomy_attributes || {};

      // Allium
      if (this.selectedAlliumAttributeSource) {
        const sourceKey = this.getSourceKey(this.selectedAlliumAttributeSource, 'allium');
        const sourceAttributes = afterData.source_predictions?.attributes?.[sourceKey];

        if (sourceAttributes) {
          clonedAfterData.attributes = Object.fromEntries(
            Object.entries(sourceAttributes).filter(([key]) => !fieldsToExclude.includes(key))
          );

          clonedAfterData.attributes_sources = {};

          for (const key in clonedAfterData.attributes) {
            const displayedValue = clonedAfterData.attributes[key];
            const sourcesWithSameValue = [sourceKey];

            const allSources = afterData.source_predictions?.attributes || {};
            for (const otherSourceKey in allSources) {
              if (otherSourceKey === sourceKey) continue;
              const otherAttributes = allSources[otherSourceKey];
              if (otherAttributes && otherAttributes[key] !== undefined) {
                const otherValue = otherAttributes[key];

                if (
                  typeof displayedValue === 'string' &&
                  typeof otherValue === 'string' &&
                  displayedValue.trim().toLowerCase() === otherValue.trim().toLowerCase()
                ) {
                  sourcesWithSameValue.push(otherSourceKey);
                } else if (Array.isArray(displayedValue) && Array.isArray(otherValue)) {
                  const displayedArray = displayedValue.map((v) => v.trim().toLowerCase()).sort();
                  const otherArray = otherValue.map((v) => v.trim().toLowerCase()).sort();
                  if (JSON.stringify(displayedArray) === JSON.stringify(otherArray)) {
                    sourcesWithSameValue.push(otherSourceKey);
                  }
                }
              }
            }

            clonedAfterData.attributes_sources[key] = sourcesWithSameValue;
          }

          clonedAfterData.attributes_matching_default = {};
          for (const key in clonedAfterData.attributes) {
            const selectedValue = clonedAfterData.attributes[key];
            const defaultValue = defaultAttributes[key];

            let matches = false;
            if (typeof selectedValue === 'string' && typeof defaultValue === 'string') {
              matches = selectedValue.trim().toLowerCase() === defaultValue.trim().toLowerCase();
            } else if (Array.isArray(selectedValue) && Array.isArray(defaultValue)) {
              const selectedArray = selectedValue.map((v) => v.trim().toLowerCase()).sort();
              const defaultArray = defaultValue.map((v) => v.trim().toLowerCase()).sort();
              matches = JSON.stringify(selectedArray) === JSON.stringify(defaultArray);
            }
            clonedAfterData.attributes_matching_default[key] = matches;
          }
        } else {
          clonedAfterData.attributes = {};
          clonedAfterData.attributes_sources = {};
          clonedAfterData.attributes_matching_default = {};
        }
      } else {
        clonedAfterData.attributes = clonedAfterData.attributes || {};
        clonedAfterData.attributes_sources =
          clonedAfterData.attributes_sources || {};
        clonedAfterData.attributes_matching_default = {};
      }

      // Manor
      if (this.selectedManorAttributeSource) {
        const sourceKey = this.getSourceKey(this.selectedManorAttributeSource, 'manor');
        const sourceAttributes =
          afterData.source_predictions?.manor_taxonomy_attributes?.[sourceKey];

        if (sourceAttributes) {
          clonedAfterData.manor_taxonomy_attributes = Object.fromEntries(
            Object.entries(sourceAttributes).filter(([key]) => !fieldsToExclude.includes(key))
          );

          clonedAfterData.manor_taxonomy_attributes_sources = {};

          for (const key in clonedAfterData.manor_taxonomy_attributes) {
            const displayedValue = clonedAfterData.manor_taxonomy_attributes[key];
            const sourcesWithSameValue = [sourceKey];

            const allSources = afterData.source_predictions?.manor_taxonomy_attributes || {};
            for (const otherSourceKey in allSources) {
              if (otherSourceKey === sourceKey) continue;

              const otherAttributes = allSources[otherSourceKey];
              if (otherAttributes && otherAttributes[key] !== undefined) {
                const otherValue = otherAttributes[key];

                if (
                  typeof displayedValue === 'string' &&
                  typeof otherValue === 'string' &&
                  displayedValue.trim().toLowerCase() === otherValue.trim().toLowerCase()
                ) {
                  sourcesWithSameValue.push(otherSourceKey);
                } else if (Array.isArray(displayedValue) && Array.isArray(otherValue)) {
                  const displayedArray = displayedValue.map((v) => v.trim().toLowerCase()).sort();
                  const otherArray = otherValue.map((v) => v.trim().toLowerCase()).sort();
                  if (JSON.stringify(displayedArray) === JSON.stringify(otherArray)) {
                    sourcesWithSameValue.push(otherSourceKey);
                  }
                }
              }
            }

            clonedAfterData.manor_taxonomy_attributes_sources[key] = sourcesWithSameValue;
          }

          clonedAfterData.manor_attributes_matching_default = {};
          for (const key in clonedAfterData.manor_taxonomy_attributes) {
            const selectedValue = clonedAfterData.manor_taxonomy_attributes[key];
            const defaultValue = defaultManorAttributes[key];

            let matches = false;
            if (typeof selectedValue === 'string' && typeof defaultValue === 'string') {
              matches = selectedValue.trim().toLowerCase() === defaultValue.trim().toLowerCase();
            } else if (Array.isArray(selectedValue) && Array.isArray(defaultValue)) {
              const selectedArray = selectedValue.map((v) => v.trim().toLowerCase()).sort();
              const defaultArray = defaultValue.map((v) => v.trim().toLowerCase()).sort();
              matches = JSON.stringify(selectedArray) === JSON.stringify(defaultArray);
            }
            clonedAfterData.manor_attributes_matching_default[key] = matches;
          }
        } else {
          clonedAfterData.manor_taxonomy_attributes = {};
          clonedAfterData.manor_taxonomy_attributes_sources = {};
          clonedAfterData.manor_attributes_matching_default = {};
        }
      } else {
        clonedAfterData.manor_taxonomy_attributes =
          clonedAfterData.manor_taxonomy_attributes || {};
        clonedAfterData.manor_taxonomy_attributes_sources =
          clonedAfterData.manor_taxonomy_attributes_sources || {};
        clonedAfterData.manor_attributes_matching_default = {};
      }

      let alliumFlaggedAttributes = {};
      let manorFlaggedAttributes = {};

      if (this.selectedFlaggingStrategy !== 'None') {
        const flaggedAllium =
          afterData.predictions_flagged?.attributes?.[this.selectedFlaggingStrategy];
        if (flaggedAllium) {
          alliumFlaggedAttributes = flaggedAllium;
        }

        const flaggedManor =
          afterData.predictions_flagged?.manor_taxonomy_attributes?.[this.selectedFlaggingStrategy];
        if (flaggedManor) {
          manorFlaggedAttributes = flaggedManor;
        }
      }

      const attributesSources = clonedAfterData.attributes_sources || {};
      const manorAttributesSources =
        clonedAfterData.manor_taxonomy_attributes_sources || {};

      const details = this.isEditedData
        ? { ...clonedAfterData, ...this.editedDetails }
        : clonedAfterData;

      return {
        ...this.mapProductData(
          details,
          [],
          details.attributes || {},
          variantData,
          true,
          []
        ),
        attributesSources,
        manorTaxonomyAttributesSources: manorAttributesSources,
        alliumFlaggedAttributes,
        manorFlaggedAttributes,
        attributesMatchingDefault: clonedAfterData.attributes_matching_default,
        manorAttributesMatchingDefault: clonedAfterData.manor_attributes_matching_default,
      };
    },
    currentProductId() {
      if (this.currentProductIndex !== null && this.filteredProductIds.length > 0) {
        return this.filteredProductIds[this.currentProductIndex];
      } else {
        return '';
      }
    },
    currentProductVariants() {
      return this.products[this.currentProductId] || {};
    },
    variantIds() {
      return Object.keys(this.currentProductVariants);
    },
    currentVariantId() {
      if (this.selectedVariantId) {
        return this.selectedVariantId;
      } else if (this.variantIds.length > 0) {
        return this.variantIds[0];
      }
      return '';
    },
    currentVariantData() {
      const variantData = this.currentProductVariants[this.currentVariantId] || {};
      return {
        ...variantData,
        'Manor URL Link': variantData['Manor URL Link'] || '',
      };
    },
    currentBeforeData() {
      const variantData = this.currentVariantData;
      if (!variantData || (!variantData.before && !variantData.before_updated)) {
        return this.emptyProductData();
      }

      let selectedBeforeData;
      if (this.beforeUpdatedSelected && variantData.before_updated) {
        selectedBeforeData = variantData.before_updated;
      } else if (variantData.before) {
        selectedBeforeData = variantData.before;
      } else {
        return this.emptyProductData();
      }

      let attributes;
      if (this.selectedLanguage === 'english') {
        attributes = selectedBeforeData.english_attributes || {};
      } else {
        attributes = selectedBeforeData.attributes || {};
      }

      const images = variantData.image_urls || selectedBeforeData.image_urls || [];
      const validImages = images.filter((img) => img && img.trim() !== '');

      const thumbnailPaths =
        variantData.thumbnail_paths || selectedBeforeData.thumbnail_paths || [];
      const thumbnails = thumbnailPaths.map((path) => {
        if (path.startsWith('/image_thumbnails/')) {
          return (
            'https://storage.googleapis.com/allium-ai-manor/demo/thumbnails/' +
            path.substring('/image_thumbnails/'.length)
          );
        }
        return path;
      });
      const validThumbnails = thumbnails.filter((img) => img && img.trim() !== '');

      let careInstructions = {};
      if (selectedBeforeData['Care Instructions']) {
        careInstructions = selectedBeforeData['Care Instructions'];
      } else if (
        selectedBeforeData[this.selectedLanguage] &&
        selectedBeforeData[this.selectedLanguage]['Care Instructions']
      ) {
        careInstructions = selectedBeforeData[this.selectedLanguage]['Care Instructions'];
      } else if (
        selectedBeforeData['english_attributes'] &&
        selectedBeforeData['english_attributes']['Care Instructions']
      ) {
        careInstructions = selectedBeforeData['english_attributes']['Care Instructions'];
      }

      return this.mapProductData(
        selectedBeforeData[this.selectedLanguage],
        validImages,
        attributes,
        variantData,
        false,
        validThumbnails,
        careInstructions
      );
    },
    languages() {
      return ['english', 'german', 'french', 'italian'];
    },
    availableColors() {
      const colors = new Set();
      for (const variantId of this.variantIds) {
        const variant = this.currentProductVariants[variantId];
        const beforeData = variant.before[this.selectedLanguage];

        const primaryColor =
          beforeData?.variant_data?.['Primary Color'] ||
          beforeData?.['Primary Color'] ||
          beforeData?.['primaryColor'];

        if (primaryColor) {
          colors.add(primaryColor);
        }
      }
      return Array.from(colors);
    },
    afterAvailableColors() {
      const colors = new Set();
      for (const variantId of this.variantIds) {
        const variant = this.currentProductVariants[variantId];
        const afterData = variant.after?.[this.selectedLanguage];
        if (!afterData) continue;

        const primaryColorHex =
          afterData.variant_data?.['Primary Color Hex Code'] ||
          afterData.variant_data?.['primaryColorHexCode'] ||
          null;

        if (primaryColorHex) {
          colors.add(primaryColorHex);
        }
      }
      return Array.from(colors);
    },
    availableSizes() {
      const sizes = new Set();
      for (const variantId of this.variantIds) {
        const variant = this.currentProductVariants[variantId];
        const beforeData = variant.before[this.selectedLanguage];

        const size = beforeData?.variant_data?.['Size'] || beforeData?.attributes?.['Size'];
        if (size) sizes.add(size);
      }
      return Array.from(sizes);
    },
    isPreviousDisabled() {
      return this.currentProductIndex === 0;
    },
    isNextDisabled() {
      return this.currentProductIndex >= this.filteredProductIds.length - 1;
    },
    alliumFlaggedAttributes() {
      return this.currentAfterData.alliumFlaggedAttributes || {};
    },
    manorFlaggedAttributes() {
      return this.currentAfterData.manorFlaggedAttributes || {};
    },
    bannerClass() {
      if (this.reviewMode && this.isCurrentlyReviewed) {
        return 'reviewed-banner';
      } else {
        return 'not-reviewed-banner';
      }
    }
  },

  methods: {
    async loadReviewStatus(variantId) {
      try {
        const folder = "manor-apparel-pilot/manual-labels2"; // your folder
        const res = await fetch(
          `${this.FUNCTION_URL}?action=load&key=${variantId}&folder=${folder}`
        );

        if (res.status === 404) {
          // Not reviewed
          console.log(`Variant ${variantId} not reviewed yet.`);
          this.reviewedMap[variantId] = { reviewed: false };
          return;
        }

        if (!res.ok) {
          console.error("Error loading review status:", res.statusText);
          return;
        }

        const json = await res.json();
        if (json.reviewed) {
          // Mark as reviewed in local memory
          this.reviewedMap[variantId] = { reviewed: true, data: json.data };
          console.log(`Variant ${variantId} was already reviewed (data loaded).`);
        } else {
          // It's unreviewed
          this.reviewedMap[variantId] = { reviewed: false };
          console.log(`Variant ${variantId} is unreviewed.`);
        }
      } catch (err) {
        console.error("Error loading review status:", err);
      }
    },

    loadCurrentPairData() {
      // Make sure we have a valid currentPairIndex
      if (
        this.currentPairIndex == null ||
        this.filteredPairs.length === 0 ||
        this.currentPairIndex >= this.filteredPairs.length
      ) {
        // No item to load
        return;
      }

      const { productId, variantId } = this.filteredPairs[this.currentPairIndex];

      // Update your component to reflect the newly-chosen product/variant
      this.currentProductId = productId;
      this.currentVariantId = variantId;

      // Example: call loadReviewStatus(variantId) to see if it’s already reviewed
      this.loadReviewStatus(productId);

      // Optionally, load images for this product/variant, etc.
      // e.g.: this.loadImagesForVariant(variantId);
    },

    isProductEdited(productId) {
      return !!this.reviewedMap[productId]?.reviewed;
    },
    openAddNotePopup() {
      this.showAddNotePopup = true;
      this.reviewNote = this.currentAfterData.Notes || '';
    },
    closeAddNotePopup() {
      this.showAddNotePopup = false;
      this.reviewNote = '';
    },
    saveNote() {
      // Put the note in "Notes" field. 
      // If you're storing "Notes" in currentAfterData:
      if (!this.currentAfterData) this.currentAfterData = {};
      this.currentAfterData.Notes = this.reviewNote;
      
      // Close popup
      this.closeAddNotePopup();
    },
    toggleSkipReviewed() {
      this.skipReviewed = !this.skipReviewed;
      if (this.skipReviewed) {
        // Make sure "Only Show Edited" is off
        this.onlyShowEditedData = false;
      }
      // Filter local data
      this.applyReviewFilters();
    },

    toggleOnlyShowEditedData() {
      this.onlyShowEditedData = !this.onlyShowEditedData;
      if (this.onlyShowEditedData) {
        // Make sure "Skip Reviewed" is off
        this.skipReviewed = false;
      }
      // Filter local data
      this.applyReviewFilters();
    },

    async fetchNextUnreviewedItem() {
      try {
        const url = `${FUNCTION_URL}?action=getNextUnreviewedItem&folder=manor-apparel-pilot/manual-labels2`;
        const response = await fetch(url);
        const data = await response.json();

        if (data.done) {
          // No more unreviewed data => show our alert
          this.showNoMoreUnreviewed = true;
          // Fade out after 3 seconds
          setTimeout(() => {
            this.showNoMoreUnreviewed = false;
          }, 3000);
          return;
        }

        // Otherwise, store the returned item...
        this.$set(this.products, data.itemId, {
          before: data.data.before,
          after: data.data.after,
        });
        this.selectedVariantId = data.itemId;
        await this.loadImagesForVariant(this.selectedVariantId);

      } catch (error) {
        console.error('Error fetching unreviewed item:', error);
      }
    },

    async fetchNextReviewedItem() {
      try {
        // 1) Call your serverless function
        const url = `${FUNCTION_URL}?action=getNextReviewedItem&folder=manor-apparel-pilot/manual-labels2`;
        const response = await fetch(url);
        const data = await response.json();

        // 2) Check if any reviewed items exist
        if (data.done) {
          alert('No more reviewed items!');
          return;
        }

        // 3) Insert the newly fetched product variant into your local `products` object,
        //    so your computed properties and watchers can pick it up:
        this.$set(this.products, data.itemId, {
          before: data.data.before,
          after: data.data.after,
          // ... add any other fields you might need (brand, image_urls, etc.)
        });

        // 4) Update your selectedVariantId (so your UI knows which variant to display)
        this.selectedVariantId = data.itemId;
        this.currentProductIndex = null; 
        this.currentVariantDataFromServer = data.data;

        // 5) Optionally load images for this variant so they stay in sync
        await this.loadImagesForVariant(this.selectedVariantId);

        console.log('Fetched reviewed item:', data.itemId);
      } catch (error) {
        console.error('Error fetching next reviewed item:', error);
      }
    },

    async loadImagesForVariant(variantId) {
      try {
        // If your server response has data.images, we can set them directly.
        if (this.currentVariantDataFromServer && this.currentVariantDataFromServer.images) {
          // e.g. store them to your "currentBeforeData" or "currentAfterData"
          this.currentBeforeData.images = this.currentVariantDataFromServer.images;
        }
        // Or fetch from the server if needed. 
        // The main point is: we only load images *after* we know which variant
        // so they stay in sync.
      } catch (err) {
        console.error('Error loading images for variant:', variantId, err);
      }
    },
    getManorOptions(key) {
      return this.manor_taxonomy_en[key] || [];
    },
    collectFlaggedAttributes() {
      const flaggedAttributesSet = new Set();

      for (const productId in this.products) {
        const variants = this.products[productId];

        for (const variantId in variants) {
          const variant = variants[variantId];
          const afterData = variant.after?.[this.selectedLanguage];

          if (afterData && afterData.predictions_flagged) {
            const alliumFlaggedAttributes = afterData.predictions_flagged.attributes?.[this.selectedFlaggingStrategy];
            if (alliumFlaggedAttributes) {
              for (const attrKey in alliumFlaggedAttributes) {
                if (alliumFlaggedAttributes[attrKey]) {
                  flaggedAttributesSet.add(attrKey);
                }
              }
            }

            const manorFlaggedAttributes = afterData.predictions_flagged.manor_taxonomy_attributes?.[this.selectedFlaggingStrategy];
            if (manorFlaggedAttributes) {
              for (const attrKey in manorFlaggedAttributes) {
                if (manorFlaggedAttributes[attrKey]) {
                  flaggedAttributesSet.add(attrKey);
                }
              }
            }
          }
        }
      }

      this.flaggedAttributesList = Array.from(flaggedAttributesSet).sort();
    },
    getSourceKey(selectedSource, type) {
      const sourceMap = {
        Image: type === 'allium' ? 'image_allium' : 'image_manor',
        Existing: type === 'allium' ? 'existing_allium' : 'existing_manor',
        Vendor: type === 'allium' ? 'vendor_allium' : 'vendor_manor',
        External: type === 'allium' ? 'external_allium' : 'external_manor',
      };
      return sourceMap[selectedSource] || '';
    },

    navigateTo(view) {
      this.currentView = view;
    },

    openProductPage({ productId, variantId }) {
      this.currentView = 'product';
      this.currentProductIndex = this.allProductIds.indexOf(productId);
      this.selectedVariantId = variantId;
    },

    goToCatalog() {
      this.currentView = 'catalog';
    },

    handleAttributeEdited({ key, value }) {
      this.editedAttributes = {
        ...this.editedAttributes,
        [key]: value,
      };
      this.hasEdits = true;
    },

    handleDetailEdited({ field, value }) {
      this.editedDetails = {
        ...this.editedDetails,
        [field]: value,
      };
      this.hasEdits = true;
    },

    toggleEditedData() {
      this.isEditedData = !this.isEditedData;
    },

    resetEdits() {
      this.editedAttributes = {};
      this.editedDetails = {};
      this.hasEdits = false;
    },

    async onLoginSuccess() {
      console.log('Login successful. Updating authentication state.');
      this.isAuthenticated = true;
      this.currentView = 'catalog';
      try {
        await this.loadProductData();
      } catch (error) {
        console.error('Error loading product data:', error);
      }
    },

    async loadProductData() {
      try {
        const fetchUrl = process.env.NODE_ENV === 'production'
          ? '/manor/demo_comparison_data_updated_taxonomy_fixed_gpt_4o_all_updated_color_normalized_updated_morgan_before_after_with_sources_claude_new_filters.json'
          : 'demo_comparison_data_updated_taxonomy_fixed_gpt_4o_all_updated_color_normalized_updated_morgan_before_after_with_sources_claude_new_filters.json';

        const response = await fetch(fetchUrl);
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();
        this.collectFlaggedAttributes();
        if (data && typeof data === 'object') {
          this.products = data;
          this.allProductIds = Object.keys(data);
          this.computeBrands();

          await this.$nextTick();
          this.buildAttributeData();

          this.filteredProductIds = [...this.allProductIds];
          if (this.filteredProductIds.length > 0) {
            this.currentProductIndex = 0;
          }

          console.log('Loaded products:', this.products);
          console.log('Built attributes:', this.attributeFilters);
        } else {
          throw new Error('Unexpected data format');
        }
      } catch (error) {
        console.error('Error loading product data:', error);
        throw error;
      }
    },

    computeBrands() {
      const brandSet = new Set();
      for (const productId of this.allProductIds) {
        const variants = this.products[productId];
        for (const variantId in variants) {
          const variant = variants[variantId];
          const brand = variant['Brand'] || 'Unknown';
          brandSet.add(brand);
        }
      }
      this.brands = Array.from(brandSet).sort();
    },

    buildAttributeData() {
      try {
        this.attributeFilters = {
          dressCategory: {
            attributes: [],
            values: {},
          },
          manorTaxonomy: {
            attributes: [],
            values: {},
          },
          alliumTaxonomy: {
            attributes: [],
            values: {},
          },
        };

        for (const productId in this.products) {
          const variants = this.products[productId];
          for (const variantId in variants) {
            const variant = variants[variantId];
            const afterData = variant.after?.[this.selectedLanguage];
            if (!afterData) continue;

            const dressCategory = afterData['Dress Category'] || afterData['dressCategory'];
            if (dressCategory) {
              if (!this.attributeFilters.dressCategory.attributes.includes('Dress Category')) {
                this.attributeFilters.dressCategory.attributes.push('Dress Category');
              }
              if (!this.attributeFilters.dressCategory.values['Dress Category']) {
                this.attributeFilters.dressCategory.values['Dress Category'] = new Set();
              }
              this.attributeFilters.dressCategory.values['Dress Category'].add(dressCategory);
            }

            const manorAttrs = afterData.manor_taxonomy_attributes || {};
            Object.entries(manorAttrs).forEach(([key, value]) => {
              if (typeof value === 'string') {
                if (!this.attributeFilters.manorTaxonomy.attributes.includes(key)) {
                  this.attributeFilters.manorTaxonomy.attributes.push(key);
                  this.attributeFilters.manorTaxonomy.values[key] = new Set();
                }
                this.attributeFilters.manorTaxonomy.values[key].add(value);
              }
            });

            const alliumAttrs = afterData.attributes || {};
            Object.entries(alliumAttrs).forEach(([key, value]) => {
              if (typeof value === 'string') {
                if (!this.attributeFilters.alliumTaxonomy.attributes.includes(key)) {
                  this.attributeFilters.alliumTaxonomy.attributes.push(key);
                  this.attributeFilters.alliumTaxonomy.values[key] = new Set();
                }
                this.attributeFilters.alliumTaxonomy.values[key].add(value);
              }
            });
          }
        }

        for (const category in this.attributeFilters) {
          this.attributeFilters[category].attributes.sort();
          for (const attr in this.attributeFilters[category].values) {
            this.attributeFilters[category].values[attr] = 
              Array.from(this.attributeFilters[category].values[attr]).sort();
          }
        }

        console.log('Built attribute filters:', this.attributeFilters);
      } catch (error) {
        console.error('Error building attribute data:', error);
      }
      this.collectFlaggedAttributes();
    },

    getAttributesForCatalog() {
      return {
        dressCategories: this.attributeFilters.dressCategory.values['Dress Category'] || [],
        manorTaxonomyAttributes: {
          attributes: this.attributeFilters.manorTaxonomy.attributes,
          values: this.attributeFilters.manorTaxonomy.values,
        },
        alliumTaxonomyAttributes: {
          attributes: this.attributeFilters.alliumTaxonomy.attributes,
          values: this.attributeFilters.alliumTaxonomy.values,
        },
      };
    },

    mapProductData(product, images, attributes, variantData = {}, isAfter = false, thumbnails = [], careInstructions = {}) {
      if (!product) {
        return this.emptyProductData();
      }

      let specifications = {
        Material: product.variant_data?.['Material'] || 'N/A',
        Pattern: product.variant_data?.['Pattern'] || 'N/A',
        Size: product.variant_data?.['Size'] || 'N/A',
        'Care Instructions': 'N/A',
      };

      if (specifications.Material === 'N/A' && attributes['Material']) {
        specifications.Material = attributes['Material'];
      }

      if (specifications.Pattern === 'N/A' && attributes['Pattern']) {
        specifications.Pattern = attributes['Pattern'];
      }

      if (specifications.Size === 'N/A' && attributes['Size']) {
        specifications.Size = attributes['Size'];
      }

      if (isAfter && product['manor_taxonomy_attributes']) {
        const manorAttrs = product['manor_taxonomy_attributes'];
        if (manorAttrs['Material']) {
          specifications['Material'] = manorAttrs['Material'];
        }
        if (manorAttrs['Pattern']) {
          specifications['Pattern'] = manorAttrs['Pattern'];
        }
      }

      if (!isAfter) {
        specifications['Care Instructions'] = careInstructions && Object.keys(careInstructions).length > 0
          ? careInstructions
          : 'N/A';
      } else {
        const care = product['Care Instructions'];
        if (care) {
          if (typeof care === 'object' && Object.keys(care).length > 0) {
            specifications['Care Instructions'] = Object.entries(care)
              .map(([key, value]) => `${key}: ${value}`)
              .join('\n');
          } else if (typeof care === 'string' && care.trim() !== '') {
            specifications['Care Instructions'] = care;
          } else {
            specifications['Care Instructions'] = 'N/A';
          }
        } else {
          specifications['Care Instructions'] = 'N/A';
        }
      }

      const primaryColor =
        product.variant_data?.['primaryColor'] ||
        product['Primary Color'] ||
        product['primaryColor'] ||
        attributes['Primary Color'] ||
        attributes['primaryColor'] ||
        'Unknown';

      let clothingType = 'Unknown';
      if (isAfter) {
        clothingType =
          product['DressType'] ||
          product['dressType'] ||
          product['manor_taxonomy_attributes']?.['ClothingType'] ||
          'Unknown';
      } else {
        clothingType = product['Dress Category'] || product['dressCategory'] || 'Unknown';
      }

      if (isAfter) {
        const variantDataObj = product.variant_data || {};
        specifications['Binned Size'] = variantDataObj['Binned Size'] || 'N/A';
        specifications['EU Size 1'] = variantDataObj['EU Size 1'] || 'N/A';
        specifications['EU Size 2'] = variantDataObj['EU Size 2'] || 'N/A';
      }

      let externalMatches = [];
      if (isAfter && variantData.after && variantData.after['External Matches']) {
        externalMatches = variantData.after['External Matches'];
      }

      return {
        title: product['Product Title'] || product['productTitle'] || 'No title available',
        description: product['Product Description'] || product['productDescription'] || 'No description available.',
        clothingType: clothingType,
        images: images,
        thumbnails: thumbnails,
        primaryColorHex:
          product.variant_data?.['Primary Color Hex Code'] ||
          product.variant_data?.['primaryColorHexCode'] ||
          null,
        primaryColor: primaryColor,
        allColors:
          product.variant_data?.['All Colors'] ||
          product.variant_data?.['allColors'] ||
          product.variant_data?.['Allcolors'] ||
          [],
        size: specifications.Size || 'N/A',
        bulletPoints: (product['Bullet Points'] || product['bulletPoints'] || []).filter(
          (point) => typeof point === 'string' && point.trim() !== ''
        ),
        specifications: specifications,
        attributes: attributes || {},
        attributesSources: product['attributes_sources'] || {},
        careInstructions: careInstructions || {},
        manorTaxonomyAttributes: product['manor_taxonomy_attributes'] || {},
        manorTaxonomyAttributesSources: product['manor_taxonomy_attributes_sources'] || {},
        externalMatches: externalMatches,
      };
    },

    emptyProductData() {
      return {
        title: 'No title available',
        description: 'No description available.',
        clothingType: 'Unknown',
        images: [],
        thumbnails: [],
        primaryColorHex: null,
        primaryColor: 'Unknown',
        allColors: [],
        size: 'N/A',
        bulletPoints: [],
        specifications: {},
        attributes: {},
        attributesSources: {},
        careInstructions: {},
        manorTaxonomyAttributes: {},
        manorTaxonomyAttributesSources: {},
        externalMatches: [],
      };
    },

    changeLanguage() {
      this.resetVariantSelection();
      this.buildAttributeData();
      this.collectFlaggedAttributes();
      this.applyBrandAndAttributeFilters();
    },

    async previousProduct() {
      if (this.currentProductIndex > 0) {
        this.currentProductIndex--;
        this.resetVariantSelection();
        this.selectedVariantId = this.variantIds[0] || '';
        await this.loadCurrentItemData();
      }
    },

    async nextProduct() {
      if (this.currentProductIndex < this.filteredProductIds.length - 1) {
        this.currentProductIndex++;
        this.resetVariantSelection();
        this.selectedVariantId = this.variantIds[0] || '';
        await this.loadCurrentItemData();
      }
    },

    selectColor(color) {
      this.selectedColor = color;
      this.updateVariantBySelection();
    },

    selectSize(size) {
      this.selectedSize = size;
      this.updateVariantBySelection();
    },

    updateVariantBySelection() {
      for (const variantId of this.variantIds) {
        const variant = this.currentProductVariants[variantId];
        const beforeData = variant.before[this.selectedLanguage];

        const variantColor =
          beforeData?.variant_data?.['Primary Color'] ||
          beforeData?.['Primary Color'] ||
          beforeData?.['primaryColor'];

        const variantSize = beforeData?.variant_data?.['Size'] || beforeData?.attributes?.['Size'];

        if (
          (!this.selectedColor || variantColor === this.selectedColor) &&
          (!this.selectedSize || variantSize === this.selectedSize)
        ) {
          this.selectedVariantId = variantId;
          return;
        }
      }
      this.selectedVariantId = this.variantIds[0] || '';
    },

    resetVariantSelection() {
      this.selectedVariantId = '';
      this.selectedColor = '';
      this.selectedSize = '';
    },

    onAttributeFilterTypeChange() {
      this.selectedAttributeFilter.attribute = '';
      this.selectedAttributeFilter.value = '';
      this.applyBrandAndAttributeFilters();
    },

    onAttributeNameChange() {
      this.selectedAttributeFilter.value = '';
      this.applyBrandAndAttributeFilters();
    },

    applyAttributeFilter() {
      this.applyBrandAndAttributeFilters();
    },

    applyBrandAndAttributeFilters() {
      let filteredIds = [...this.allProductIds];

      // Apply brand filter
      if (this.selectedBrand) {
        filteredIds = filteredIds.filter((productId) => {
          const variants = this.products[productId];
          return Object.values(variants).some(
            variant => variant['Brand'] === this.selectedBrand
          );
        });
      }

      // Apply attribute filter
      if (
        this.selectedAttributeFilter.type &&
        this.selectedAttributeFilter.attribute &&
        this.selectedAttributeFilter.value
      ) {
        filteredIds = filteredIds.filter((productId) => {
          const variants = this.products[productId];
          return Object.values(variants).some(variant => {
            const afterData = variant.after?.[this.selectedLanguage];
            if (!afterData) return false;

            if (this.selectedAttributeFilter.type === 'dressCategory') {
              const dressCategory = afterData['Dress Category'] || afterData['dressCategory'];
              return dressCategory === this.selectedAttributeFilter.value;
            } else if (this.selectedAttributeFilter.type === 'manorTaxonomy') {
              const manorAttrs = afterData.manor_taxonomy_attributes || {};
              return manorAttrs[this.selectedAttributeFilter.attribute] === this.selectedAttributeFilter.value;
            } else if (this.selectedAttributeFilter.type === 'alliumTaxonomy') {
              const alliumAttrs = afterData.attributes || {};
              return alliumAttrs[this.selectedAttributeFilter.attribute] === this.selectedAttributeFilter.value;
            }
            return false;
          });
        });
      }

      // Apply flagged attribute filter
      if (this.selectedFlaggedAttribute) {
        filteredIds = filteredIds.filter((productId) => {
          const variants = this.products[productId];
          return Object.values(variants).some((variant) => {
            const afterData = variant.after?.[this.selectedLanguage];
            if (!afterData) return false;

            const predictionsFlagged = afterData.predictions_flagged;
            const alliumFlaggedAttributes = predictionsFlagged?.attributes?.[this.selectedFlaggingStrategy];
            const manorFlaggedAttributes = predictionsFlagged?.manor_taxonomy_attributes?.[this.selectedFlaggingStrategy];

            return (
              (alliumFlaggedAttributes && alliumFlaggedAttributes[this.selectedFlaggedAttribute]) ||
              (manorFlaggedAttributes && manorFlaggedAttributes[this.selectedFlaggedAttribute])
            );
          });
        });
      }

      this.filteredProductIds = filteredIds;
      this.currentProductIndex = this.filteredProductIds.length > 0 ? 0 : null;
      this.resetVariantSelection();
    },

    onBrandChange() {
      this.applyBrandAndAttributeFilters();
    },

    onVariantSearch() {
      const variantNumber = this.variantSearchInput.trim();
      if (!variantNumber) return;

      for (let i = 0; i < this.allProductIds.length; i++) {
        const productId = this.allProductIds[i];
        const variants = this.products[productId];
        if (variants && variants[variantNumber]) {
          const filteredIndex = this.filteredProductIds.indexOf(productId);
          if (filteredIndex === -1) {
            this.filteredProductIds.splice(this.currentProductIndex + 1, 0, productId);
            this.currentProductIndex = this.filteredProductIds.indexOf(productId);
          } else {
            this.currentProductIndex = filteredIndex;
          }
          this.selectedVariantId = variantNumber;
          return;
        }
      }
      alert('Variant not found.');
    },

    changeManorReweightedMethod() {
    },

    changeAlliumReweightedMethod() {
    },

    changeFlaggingStrategy() {
      this.selectedFlaggedAttribute = '';
      this.collectFlaggedAttributes();
      this.applyBrandAndAttributeFilters();
    },

    formatOptionLabel(option) {
      return option
        .replace(/_/g, ' ')
        .replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1));
    },

    toggleReviewMode() {
      this.reviewMode = !this.reviewMode;

      // If we are *exiting* review mode, reset skip/onlyShow
      if (!this.reviewMode) {
        this.skipReviewed = false;
        this.onlyShowEditedData = false;
        // Possibly showReviewedData = true, etc.
        this.applyReviewFilters(); // or applyBrandAndAttributeFilters()
      }
    },

    initManorAttributesForReview() {
      this.editedManorAttributes = {};
      const attrs = { ...this.currentAfterData.manorTaxonomyAttributes };

      // Ensure all keys appear even if null
      for (const k of Object.keys(this.manor_taxonomy_en)) {
        if (attrs[k] === undefined) {
          attrs[k] = null;
        }
      }

      for (const key in attrs) {
        this.editedManorAttributes[key] = { value: attrs[key], confidence: 'certain' };
      }

      // If user toggled "Show Reviewed Data" and the product is currently reviewed,
      // let's load from reviewedMap[this.currentProductId].data
      if (this.reviewMode && this.showReviewedData && this.isCurrentlyReviewed) {
        const reviewedAttrs =
          this.reviewedMap[this.currentProductId]?.data?.[this.currentVariantId]?.after?.[
            this.selectedLanguage
          ]?.manor_taxonomy_attributes || {};

        for (const k in reviewedAttrs) {
          if (typeof reviewedAttrs[k] === 'object' && reviewedAttrs[k].value) {
            this.editedManorAttributes[k] = {
              value: reviewedAttrs[k].value,
              confidence: reviewedAttrs[k].confidence || 'certain'
            };
          } else {
            this.editedManorAttributes[k] = {
              value: reviewedAttrs[k],
              confidence: 'certain'
            };
          }
        }
      }
    },

    cycleConfidence(key) {
      const current = this.editedManorAttributes[key].confidence;
      let nextIndex = (this.confidenceOrder.indexOf(current) + 1) % this.confidenceOrder.length;
      this.editedManorAttributes[key].confidence = this.confidenceOrder[nextIndex];
    },
    confidenceClass(confidence) {
      return `confidence-${confidence}`;
    },

    async applyReviewFilters() {
      // 1) Remember the currently selected productId and variantId
      const oldProductId = this.currentProductId;
      const oldVariantId = this.currentVariantId; 

      // Make a copy of all product IDs
      let filteredIds = [...this.allProductIds];

      // Skip reviewed if reviewMode + skipReviewed
      if (this.reviewMode && this.skipReviewed) {
        filteredIds = filteredIds.filter((productId) => {
          return !this.reviewedMap[productId]?.reviewed;
        });
      }

      // Only show edited if requested
      if (this.reviewMode && this.onlyShowEditedData) {
        filteredIds = filteredIds.filter((productId) => this.isProductEdited(productId));
      }

      // Brand filter
      if (this.selectedBrand) {
        filteredIds = filteredIds.filter((productId) => {
          const variants = this.products[productId];
          return Object.values(variants).some(
            (variant) => variant['Brand'] === this.selectedBrand
          );
        });
      }

      // Flagged attribute filter
      if (this.selectedFlaggedAttribute) {
        filteredIds = filteredIds.filter((productId) => {
          const variants = this.products[productId];
          return Object.values(variants).some((variant) => {
            const afterData = variant.after?.[this.selectedLanguage];
            if (!afterData) return false;

            const predictionsFlagged = afterData.predictions_flagged;
            const alliumFlaggedAttributes =
              predictionsFlagged?.attributes?.[this.selectedFlaggingStrategy];
            const manorFlaggedAttributes =
              predictionsFlagged?.manor_taxonomy_attributes?.[this.selectedFlaggingStrategy];

            return (
              (alliumFlaggedAttributes && alliumFlaggedAttributes[this.selectedFlaggedAttribute]) ||
              (manorFlaggedAttributes && manorFlaggedAttributes[this.selectedFlaggedAttribute])
            );
          });
        });
      }

      // Attribute filter
      if (
        this.selectedAttributeFilter.type &&
        this.selectedAttributeFilter.attribute &&
        this.selectedAttributeFilter.value
      ) {
        filteredIds = filteredIds.filter((productId) => {
          const variants = this.products[productId];
          return Object.values(variants).some((variant) => {
            const afterData = variant.after?.[this.selectedLanguage];
            if (!afterData) return false;

            if (this.selectedAttributeFilter.type === 'dressCategory') {
              const dressCategory = afterData['Dress Category'] || afterData['dressCategory'];
              return dressCategory === this.selectedAttributeFilter.value;
            } else if (this.selectedAttributeFilter.type === 'manorTaxonomy') {
              const manorAttrs = afterData.manor_taxonomy_attributes || {};
              return manorAttrs[this.selectedAttributeFilter.attribute] === this.selectedAttributeFilter.value;
            } else if (this.selectedAttributeFilter.type === 'alliumTaxonomy') {
              const alliumAttrs = afterData.attributes || {};
              return alliumAttrs[this.selectedAttributeFilter.attribute] === this.selectedAttributeFilter.value;
            }
            return false;
          });
        });
      }

      // 2) Update filteredProductIds
      this.filteredProductIds = filteredIds;

      // 3) If no results left, bail
      if (this.filteredProductIds.length === 0) {
        this.currentProductIndex = null;
        return;
      }

      // 4) Keep old productId if valid
      const stillValidIndex = this.filteredProductIds.indexOf(oldProductId);
      if (stillValidIndex !== -1) {
        this.currentProductIndex = stillValidIndex;
      } else {
        this.currentProductIndex = 0;
      }

      // 5) Possibly preserve the oldVariantId if it still exists
      const newCurrentProductId = this.filteredProductIds[this.currentProductIndex];
      const newVariantsObj = this.products[newCurrentProductId] || {};
      const variantIds = Object.keys(newVariantsObj);
      if (oldVariantId && variantIds.includes(oldVariantId)) {
        this.selectedVariantId = oldVariantId;
      } else {
        this.selectedVariantId = variantIds[0] || '';
      }

      // 6) load the newly-chosen item data
      await this.loadCurrentItemData();
    },


    initManorAttributesFromReviewedData(productId) {
      const reviewedAttrs = this.reviewedMap[productId]?.data?.manor_taxonomy_attributes || {};
      const newObj = {};
      for (const k in reviewedAttrs) {
        if (typeof reviewedAttrs[k] === 'object' && reviewedAttrs[k].value) {
          newObj[k] = { value: reviewedAttrs[k].value, confidence: reviewedAttrs[k].confidence || 'certain' };
        } else {
          newObj[k] = { value: reviewedAttrs[k], confidence: 'certain' };
        }
      }
      this.editedManorAttributes = newObj;
    },

    async loadCurrentItemData() {
      if (!this.currentVariantId) return;

      const key = this.currentVariantId;
      const folder = 'manor-apparel-pilot/manual-labels2';

      try {
        const res = await fetch(`${FUNCTION_URL}?action=load&key=${key}&folder=${folder}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' }
        });

        if (res.ok) {
          const rd = await res.json();
          if (rd.reviewed) {
            // Store the entire data in reviewedMap
            this.reviewedMap[key] = { reviewed: true, data: rd.data };

            // MERGE any existing "Notes" into products[...] so currentAfterData sees it
            if (rd.data.Notes) {
              // Find the relevant product/variant data
              const productId = this.currentProductId; // or wherever you track which product
              const variantObj = this.products[productId][key];

              // Ensure 'after' object exists for the selected language
              if (!variantObj.after) variantObj.after = {};
              if (!variantObj.after[this.selectedLanguage]) {
                variantObj.after[this.selectedLanguage] = {};
              }

              // Merge the Notes
              variantObj.after[this.selectedLanguage].Notes = rd.data.Notes;
            }
          } else {
            this.reviewedMap[key] = { reviewed: false };
          }
        } else if (res.status === 404) {
          this.reviewedMap[key] = { reviewed: false };
        } else {
          console.error('Error loading reviewed data:', await res.text());
          this.reviewedMap[key] = { reviewed: false };
        }
      } catch (error) {
        console.error("Error loading reviewed data:", error);
        this.reviewedMap[key] = { reviewed: false };
      }

      // Re-initialize your Manor attributes
      this.initManorAttributesForReview();
    },

    async saveChanges() {
      // 1) Guard checks
      if (!this.isAuthenticated || this.currentView !== 'product') return;

      if (!this.reviewMode) {
        console.log('Save Changes clicked, but not in review mode');
        return;
      }

      // 2) Use the PRODUCT ID as the key
      const productId = this.currentProductId;
      const folder = 'manor-apparel-pilot/manual-labels2';

      // 3) Ensure we have placeholders for all manor taxonomy keys if needed
      for (const k of Object.keys(this.manor_taxonomy_en)) {
        if (!this.editedManorAttributes[k]) {
          this.editedManorAttributes[k] = { value: null, confidence: 'certain' };
        }
      }

      // 4) Build the final dictionary of (attribute -> {value, confidence})
      const finalAttributes = {};
      for (const k in this.editedManorAttributes) {
        finalAttributes[k] = {
          value: this.editedManorAttributes[k].value,
          confidence: this.editedManorAttributes[k].confidence
        };
      }

      // 5) Construct a minimal object for THIS variant only
      //    We store just the necessary fields for "english" (or your selectedLanguage).
      //    You can add more fields if you also want to store Notes, etc.
      const minimalData = {
        variantId: this.currentVariantId,           // So we know which variant we updated
        language: 'english',                       // Or this.selectedLanguage if dynamic
        manor_taxonomy_attributes: finalAttributes,
        Notes: this.currentAfterData.Notes         // optional if you want to store notes
      };

      try {
        // 6) POST to the Cloud Function with productId as the key, minimalData as the content
        const response = await fetch(`${FUNCTION_URL}?action=save&folder=${folder}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            key: productId,  // <-- writing to product-level file
            data: minimalData
          })
        });

        if (response.ok) {
          console.log('Data saved successfully to', folder);

          // 7) Mark THIS product as reviewed in local memory, referencing minimalData
          this.reviewedMap[productId] = { reviewed: true, data: minimalData };

          // 8) Provide feedback to user
          this.showFeedback('Save Successful!', 'success');

          // 9) If skipReviewed is ON, re-apply filters so product disappears from the list
          if (this.skipReviewed) {
            await this.applyReviewFilters();
          }

          // 10) Optionally jump to next product after 1.5 seconds
          setTimeout(() => {
            this.nextProduct();
          }, 1500);

        } else {
          const text = await response.text();
          console.error('Error saving data:', text);
          this.showFeedback('Save Failed!', 'error');
        }
      } catch (error) {
        console.error('Exception while saving:', error);
        this.showFeedback('Save Failed!', 'error');
      }
    },


    showFeedback(message, color = 'success') {
      this.snackbarMessage = message;
      this.snackbarColor = color;
      this.showSnackbar = true;
      setTimeout(() => {
        this.showSnackbar = false;
      }, 2000);
    },

    async loadReviewStatus(productId) {
      try {
        const folder = 'manor-apparel-pilot/manual-labels2';
        const res = await fetch(`${FUNCTION_URL}?action=load&key=${productId}&folder=${folder}`);

        if (res.ok) {
          const json = await res.json();

          if (json.reviewed) {
            // Mark the product as reviewed in local memory
            this.reviewedMap[productId] = { reviewed: true, data: json.data };

            // MERGE the "after" data from json.data into our local products
            if (!this.products[productId]) {
              // Possibly the local JSON didn't have this product; just set it
              this.$set(this.products, productId, json.data);
            } else {
              // Overwrite or merge each variant's after data
              for (const variantId in json.data) {
                if (!this.products[productId][variantId]) {
                  this.$set(this.products[productId], variantId, json.data[variantId]);
                } else {
                  // If we want to preserve the existing "before" data but update "after"
                  if (json.data[variantId].after) {
                    this.$set(this.products[productId][variantId], 'after', {
                      ...this.products[productId][variantId].after,
                      ...json.data[variantId].after
                    });
                  }
                }
              }
            }
          } else {
            this.reviewedMap[productId] = { reviewed: false };
          }
        } else if (res.status === 404) {
          // Not reviewed
          this.reviewedMap[productId] = { reviewed: false };
        }
      } catch (err) {
        console.error('Error loading review status:', err);
      }
    },

    updateAttributes(updatedAttributes) {
      Object.keys(updatedAttributes).forEach(categoryKey => {
        if (this.attributeFilters[categoryKey]) {
          this.attributeFilters[categoryKey].attributes = Array.from(new Set([
            ...this.attributeFilters[categoryKey].attributes,
            ...updatedAttributes[categoryKey].attributes
          ]));

          for (const [attrName, values] of Object.entries(updatedAttributes[categoryKey].values)) {
            if (!this.attributeFilters[categoryKey].values[attrName]) {
              this.attributeFilters[categoryKey].values[attrName] = [];
            }
            this.attributeFilters[categoryKey].values[attrName] = Array.from(new Set([
              ...this.attributeFilters[categoryKey].values[attrName],
              ...values
            ])).sort();
          }
        } else {
          this.$set(this.attributeFilters, categoryKey, updatedAttributes[categoryKey]);
        }
      });

      this.buildAttributeData();
      this.applyBrandAndAttributeFilters();
    },

    handleScroll() {
      if (!this.isLoading && this.hasMore) {
        const scrollPosition = window.innerHeight + window.scrollY;
        const bottomOfPage = document.documentElement.offsetHeight - 200;
        
        if (scrollPosition >= bottomOfPage) {
          this.loadMoreProducts();
        }
      }
    },

    loadMoreProducts() {
      if (this.isLoading || !this.hasMore) return;

      this.isLoading = true;
      const startIndex = this.displayedProducts.length;
      const endIndex = startIndex + this.pageSize;
      
      setTimeout(() => {
        const nextProducts = this.filteredProducts.slice(startIndex, endIndex);
        this.displayedProducts.push(...nextProducts);
        
        this.hasMore = this.displayedProducts.length < this.filteredProducts.length;
        this.isLoading = false;
      }, 500);
    },

    checkAuthentication() {
      const isAuth = localStorage.getItem('authenticated') === 'true';
      if (isAuth && !this.isAuthenticated) {
        this.isAuthenticated = true;
        this.currentView = 'catalog';
        this.loadProductData();
      } else if (!isAuth && this.isAuthenticated) {
        this.isAuthenticated = false;
        this.currentView = 'login';
      }
    },

    initializeApp() {
      this.checkAuthentication();
      if (this.isAuthenticated) {
        this.loadProductData();
      }
    }
  },
  async mounted() {
    this.isAuthenticated = localStorage.getItem('authenticated') === 'true';
    if (this.isAuthenticated) {
      await this.loadProductData();
      // Then load review status for each product, or just do it on demand
      for (const pId of this.allProductIds) {
        this.loadReviewStatus(pId);
      }
    }
  },
  watch: {
    reviewMode(newVal) {
      if (newVal) {
        // Turn on Show Updated Data by default when entering review mode
        this.beforeUpdatedSelected = true;
        this.initManorAttributesForReview();
      } else {
        this.beforeUpdatedSelected = false;
        this.editedManorAttributes = {};
      }
      // Do not re-filter just because reviewMode changed, keep current product in place
      // Only apply filters if skipReviewed is true
      if (this.reviewMode && this.skipReviewed) {
        this.applyReviewFilters();
      }
    },
    showReviewedData(newVal) {
      // Only re-apply filters if skipReviewed is active
      if (this.reviewMode && this.skipReviewed) {
        this.applyReviewFilters();
      } else {
        // If not skipping reviewed, just refresh manor attributes
        this.initManorAttributesForReview();
      }
    },
    skipReviewed(newVal) {
      // If in review mode and skipReviewed changed, re-apply filters
      if (this.reviewMode) {
        this.applyReviewFilters();
      }
    }
  },
};
</script>

<style scoped>
.product-page {
  font-family: 'Inter', sans-serif;
  color: #2c3e50; /* Dark Blue */
  background-color: #ffffff; /* White */
}

.main-header {
  position: fixed;
  display: flex;
  flex-direction: row;
  align-items: center;
  top: 0;
  left: 0;
  width: 100%;
  background-color: #2c3e50; /* Hardcoded Dark Blue */
  color: #fff;
  padding: 20px;
  text-align: center;
  z-index: 1000;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.main-header h1 {
  margin: 0;
  font-size: 28px;
  font-weight: 700;
}

.save-button {
  position: absolute;
  top: 20px;
  right: 20px;
  background-color: #2c3e50; /* Dark Blue */
  color: #fff;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 16px;
  border-radius: 5px;
}

.save-button:hover {
  background-color: #34495e;
}

.main-content {
  margin-top: 0; /* Adjust based on header height */
  display: flex;
}

.images-and-controls {
  display: flex;
}

.images-container {
  width: 500px; /* Adjusted width between 400px and 600px */
  padding: 20px;
  box-sizing: border-box;
  background-color: #fff;
  border-right: 1px solid #bdc3c7;
}

.page-navigation {
  margin-top: 20px;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
}

.navigation-text {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.language-selector,
.flagging-strategy-selector,
.data-toggle {
  margin-top: 20px;
  text-align: center;
}

.content-container {
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  max-height: calc(100vh - 90px); /* Full viewport height minus header */
}

.comparison-container {
  display: flex;
  flex-direction: row;
  gap: 30px;
  margin-bottom: 50px;
}

.comparison-section {
  flex: 1; /* Make sure both sections take equal space */
  background-color: #fff;
  padding: 30px;
  border-radius: 15px;
  box-sizing: border-box;
  box-shadow: 0 10px 20px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 32px;
  font-weight: 700;
  margin-bottom: 30px;
  color: #2c3e50; /* Hardcoded Dark Blue */
  text-align: center;
}

.panel {
  margin-bottom: 30px;
  overflow-x: auto; /* Add horizontal scroll if content overflows */
}

.footer {
  padding: 20px;
  text-align: center;
  background-color: #2c3e50; /* Hardcoded Dark Blue */
  color: #fff;
}

.loading-state {
  text-align: center;
  margin-top: 100px;
  font-size: 20px;
  color: #2c3e50; /* Hardcoded Dark Blue */
}

.nav-button {
  background-color: #2c3e50; /* Hardcoded Dark Blue */
  border: none;
  padding: 8px;
  margin: 0 10px;
  border-radius: 50%;
  cursor: pointer;
  transition: background-color 0.3s, box-shadow 0.3s;
  width: 48px;
  height: 48px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.nav-button svg {
  width: 24px;
  height: 24px;
  stroke: #fff; /* Icon color set to white */
}

.nav-button:hover:not(:disabled) {
  background-color: #34495e; /* Slightly lighter shade on hover */
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2); /* Add box-shadow on hover */
}

.nav-button:disabled {
  background-color: #ccc;
  cursor: not-allowed;
}

.nav-button:disabled svg {
  stroke: #666; /* Icon color when disabled */
}

.article-number-text {
  font-size: 18px;
  font-weight: bold;
  color: #2c3e50; /* Hardcoded Dark Blue */
}

.variant-number-text {
  display: block;
  font-size: 16px;
  font-weight: bold;
  color: #2c3e50; /* Hardcoded Dark Blue */
  margin-top: 5px;
  text-align: center;
}

.web-link {
  margin-top: 5px;
  text-align: center;
}

.web-link a {
  color: #e67e22; /* Hardcoded Accent Color */
  text-decoration: underline;
  font-size: 16px;
}

.brand-filter,
.variant-search,
.flagging-strategy-selector,
.reweighted-selector {
  margin-top: 20px;
  text-align: center;
}

.brand-filter label,
.variant-search label,
.flagging-strategy-selector label,
.reweighted-selector label {
  margin-right: 10px;
  font-weight: bold;
}

.variant-search input {
  padding: 5px;
  width: 200px;
}

.variant-search button {
  padding: 5px 10px;
  margin-left: 5px;
  background-color: #2c3e50;
  color: #fff;
  border: none;
  cursor: pointer;
}

.variant-search button:hover {
  background-color: #34495e;
}

/* Styles for the toggle switch */
.toggle-switch {
  position: relative;
  display: inline-block;
  width: 40px;
  height: 20px;
  margin-left: 10px;
}

.toggle-switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-switch .slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #ccc;
  transition: 0.4s;
  border-radius: 20px;
}

.toggle-switch .slider:before {
  position: absolute;
  content: '';
  height: 14px;
  width: 14px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  transition: 0.4s;
  border-radius: 50%;
}

.toggle-switch input:checked + .slider {
  background-color: #2196f3;
}

.toggle-switch input:checked + .slider:before {
  transform: translateX(20px);
}

.toggle-label {
  margin-left: 8px;
  font-size: 14px;
  vertical-align: middle;
}

/* Styles for the attribute filters */
.attribute-filter {
  margin-top: 20px;
  text-align: center;
}

.attribute-filter label {
  margin-right: 10px;
  font-weight: bold;
}

/* Media Queries */
@media (max-width: 1024px) {
  .main-content {
    flex-direction: column;
  }

  .images-and-controls {
    flex-direction: column;
  }

  .images-container {
    width: 100%;
    border-right: none;
    border-bottom: 1px solid #bdc3c7;
  }

  .comparison-container {
    flex-direction: column;
  }

  .content-container {
    max-height: none;
  }

  .page-navigation {
    flex-direction: column;
  }

  .back-button {
    position: absolute;
    top: 100px; /* Adjust as needed */
    left: 20px;
    background-color: transparent;
    color: #2c3e50;
    border: none;
    font-size: 18px;
    cursor: pointer;
  }

  .back-button:hover {
    text-decoration: underline;
  }

  .flagged-attribute-filter {
    margin-top: 20px;
    text-align: center;
  }

  .flagged-attribute-filter label {
    margin-right: 10px;
    font-weight: bold;
  }
}

/* Review Mode and Manor Attributes Grid Styles (outside media query) */
.review-mode-container {
  margin: 20px 0;
  text-align: center;
}

.review-mode-button {
  background-color: #ccc;
  color: #333;
  border: none;
  padding: 10px 20px;
  font-size: 16px;
  border-radius: 5px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.review-mode-button:hover {
  background-color: #bbb;
}

.review-mode-button.active {
  background-color: #2196f3;
  color: #fff;
}

.manor-attributes-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 20px;
  margin-top: 20px;
}

.manor-attribute-row {
  display: flex;
  flex-direction: column;
}

.manor-attribute-label {
  font-weight: 600;
  margin-bottom: 5px;
}

.manor-attribute-select {
  padding: 8px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
}

.manor-attribute-select:focus {
  border-color: #2196f3;
  outline: none;
  box-shadow: 0 0 0 2px rgba(33,150,243,0.2);
}
.confidence-indicator {
  width: 12px;
  height: 12px;
  display: inline-block;
  margin-left: 8px;
  border-radius: 50%;
  cursor: pointer;
  vertical-align: middle;
}
.confidence-certain {
  background-color: #28a745; /* green */
}
.confidence-pretty_sure {
  background-color: #ffc107; /* yellow */
}
.confidence-not_sure {
  background-color: #dc3545; /* red */
}
.attribute-row-content {
  display: flex;
  align-items: center;
}
.snackbar {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: #333;
  color: #fff;
  padding: 10px 20px;
  border-radius: 5px;
  z-index: 9999;
  font-size: 14px;
  opacity: 0.9;
  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
}
.snackbar.success {
  background: #28a745; /* green */
}
.snackbar.error {
  background: #dc3545; /* red */
}

.review-mode-toggles {
  display: inline-flex;
  gap: 20px;
  margin-left: 20px;
  align-items: center;
}

.review-mode-toggles .toggle-label {
  color: #333;
  font-size: 14px;
  vertical-align: middle;
  cursor: pointer;
}
.review-mode-toggles .toggle-label input[type="checkbox"] {
  margin-right: 5px;
}

/* Improved styling for Manor Attributes in review mode */
.manor-attributes-grid-improved {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 25px;
  margin-top: 20px;
  background: #f7f9fc;
  padding: 20px;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
}

.manor-attribute-row-improved {
  display: flex;
  flex-direction: column;
}

.manor-attribute-label-improved {
  font-weight: 600;
  margin-bottom: 5px;
  font-size: 14px;
  color: #2c3e50;
}

.attribute-row-content-improved {
  display: flex;
  align-items: center;
  gap: 10px;
}

.manor-attribute-select-improved {
  padding: 8px;
  font-size: 14px;
  border: 1px solid #ccc;
  border-radius: 4px;
  width: 100%;
  background: #fff;
}

.manor-attribute-select-improved:focus {
  border-color: #2196f3;
  outline: none;
  box-shadow: 0 0 0 2px rgba(33,150,243,0.2);
}

.confidence-indicator {
  width: 14px;
  height: 14px;
  display: inline-block;
  border-radius: 50%;
  cursor: pointer;
  transition: transform 0.2s ease;
}
.confidence-indicator:hover {
  transform: scale(1.2);
}

.confidence-certain {
  background-color: #28a745; /* green */
}
.confidence-pretty_sure {
  background-color: #ffc107; /* yellow/orange */
}
.confidence-not_sure {
  background-color: #dc3545; /* red */
}

.manor-title {
  font-size: 18px;
  font-weight: bold;
  margin-top: 20px;
  margin-bottom: 15px;
  color: #2c3e50;
  border-bottom: 1px solid #ccc;
  padding-bottom: 5px;
}

/* Snackbar styling */
.snackbar {
  position: fixed;
  bottom: 20px;
  right: 20px;
  background: #333;
  color: #fff;
  padding: 10px 20px;
  border-radius: 5px;
  z-index: 9999;
  font-size: 14px;
  opacity: 0.9;
  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
}
.snackbar.success {
  background: #28a745; /* green */
}
.snackbar.error {
  background: #dc3545; /* red */
}
.snackbar.warning {
  background: #ffc107; /* yellow/orange */
}
.reviewed-banner {
  background-color: #28a745 !important; /* Green if reviewed */
}
.not-reviewed-banner {
  background-color: #2c3e50 !important; /* Original color if not reviewed */
}
.right-panel {
  position: fixed;
  top: 100px;
  right: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  z-index: 1000;
}

.toggle-button {
  background-color: #ccc;
  color: #333;
  border: none;
  padding: 8px 16px;
  font-size: 14px;
  border-radius: 5px;
  cursor: pointer;
  transition: background-color 0.3s ease;
  text-align: center;
  font-weight: 500;
}

.toggle-button:hover {
  background-color: #bbb;
}

.toggle-button.active {
  background-color: #2196f3;
  color: #fff;
}

.toggles-container {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.modal-overlay {
  position: fixed;
  top: 0; left: 0;
  width: 100%; height: 100%;
  background-color: rgba(0,0,0,0.4);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
}
.modal-content {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  width: 400px;
}
.modal-buttons {
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

/* A simple fade transition for v-if/v-else banners */
.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}

/* Our alert banner styling */
.alert-banner {
  position: fixed;
  top: 80px; /* Adjust to appear under your header if needed */
  left: 50%;
  transform: translateX(-50%);
  background-color: #ffb200; /* a golden/yellow color */
  color: #fff;
  padding: 12px 20px;
  border-radius: 4px;
  font-size: 16px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  z-index: 9999;
}
</style>

